/**
 * Converts UTC datetime values in <time> elements to the user's local timezone.
 * Elements must have a `datetime` attribute with an ISO 8601 UTC value and one
 * of the opt-in attributes below:
 *   - data-ebc-localdate    → absolute "18 May 2026 at 03:04 AEST"
 *   - data-ebc-relativedate → relative "2 days ago" (with the absolute form set
 *                             as the element's title so it shows on hover)
 *
 * Server-rendered fallback (the text content before this script runs) should be
 * an honest, low-precision string — e.g. day-only — since the imprecision is
 * the point of the relative variant.
 */
export class LocalDateFormatter {
    private static readonly absoluteFormatter = new Intl.DateTimeFormat(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
    });

    private static readonly relativeFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

    public static initialize(): void {
        document.addEventListener('DOMContentLoaded', () => {
            document.querySelectorAll<HTMLTimeElement>('time[data-ebc-localdate]')
                .forEach(el => LocalDateFormatter.formatAbsolute(el));
            document.querySelectorAll<HTMLTimeElement>('time[data-ebc-relativedate]')
                .forEach(el => LocalDateFormatter.formatRelative(el));
        });
    }

    private static formatAbsolute(el: HTMLTimeElement): void {
        const date = LocalDateFormatter.parse(el);
        if (!date) return;
        el.textContent = LocalDateFormatter.absolute(date);
    }

    private static formatRelative(el: HTMLTimeElement): void {
        const date = LocalDateFormatter.parse(el);
        if (!date) return;

        const diffSec = Math.round((date.getTime() - Date.now()) / 1000);
        const absSec = Math.abs(diffSec);

        let value: number;
        let unit: Intl.RelativeTimeFormatUnit;
        if (absSec < 60) { value = diffSec; unit = 'second'; }
        else if (absSec < 3600) { value = Math.round(diffSec / 60); unit = 'minute'; }
        else if (absSec < 86400) { value = Math.round(diffSec / 3600); unit = 'hour'; }
        else if (absSec < 604800) { value = Math.round(diffSec / 86400); unit = 'day'; }
        else if (absSec < 2592000) { value = Math.round(diffSec / 604800); unit = 'week'; }
        else if (absSec < 31536000) { value = Math.round(diffSec / 2592000); unit = 'month'; }
        else { value = Math.round(diffSec / 31536000); unit = 'year'; }

        el.textContent = LocalDateFormatter.relativeFormatter.format(value, unit);
        el.title = LocalDateFormatter.absolute(date);
    }

    private static parse(el: HTMLTimeElement): Date | null {
        const iso = el.getAttribute('datetime');
        if (!iso) return null;
        const date = new Date(iso);
        return isNaN(date.getTime()) ? null : date;
    }

    private static absolute(date: Date): string {
        // Strip the space some locales render between time digits and the am/pm designator
        // (covers pm/PM/p.m. and the U+202F narrow no-break space).
        return LocalDateFormatter.absoluteFormatter.format(date).replace(/(\d)\s+([ap]\.?m\.?)/gi, '$1$2');
    }
}

LocalDateFormatter.initialize();
